import styled from 'styled-components'

const skeletonAnimation = `
  @keyframes shimmer {
    0% {
      background-position: -480px 0;
    }
    100% {
      background-position: 480px 0;
    }
  }
`

export const HeaderContainer = styled.div`
  position: relative;
  z-index: 20;
  ${skeletonAnimation}
`

export const HeaderSurface = styled.div<{ $isSelected?: boolean }>`
  width: 617px;
  height: 76px;
  border-radius: 40px;
  background: ${({ theme }) => theme.colors.card.background};
  display: flex;
  padding-left: 15px;
  padding-right: 15px;
  gap: 26px;
  box-sizing: border-box;
  transform: translateZ(0);
  transform-origin: center;
  will-change: transform, background-color;
  transition: ${({ theme }) =>
    `transform ${theme.animation.duration} ${theme.animation.style}`};

  @media (max-width: 640px) {
    border-radius: 20px;
    width: min(617px, calc(100vw - 24px));
    max-width: calc(100vw - 24px);
    min-height: 76px;
    height: auto;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-template-areas:
      'classroom classroom classroom'
      'objectives summatives right';
    justify-items: center;
    align-items: center;
    gap: 12px;
    padding-top: 10px;
    padding-bottom: 10px;
  }

  ${(props) =>
    props.$isSelected &&
    `
      &:hover {
        cursor: pointer;
        background-color: ${props.theme.colors.button.primary};
        transform: scale(1.01);

        .hover__color,
        .summative__levels * {
          // using important because elements might have their own colors
          color: white !important;
        }

        .classroom__card {
          background-color: ${props.theme.colors.button.primary} !important;
        }

        /**
         * Keep the LFQ progress track visible against the blue classroom-row
         * hover surface, using the matching light or dark card neutral.
         */
        .lfq-formatives > div > div:first-child {
          background-color: ${props.theme.colors.card.background} !important;
          border: 1px solid ${props.theme.colors.card.background};
        }

        /**
         * Keep the LFQ completion value readable on the blue hover surface
         * without adding a background to the label.
         */
        .lfq-formatives .hover__color {
          color: white !important;
        }
      }
    `}
`

export const ClassroomSection = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 10px;
  gap: 4px;

  @media (max-width: 640px) {
    width: 100%;
    grid-area: classroom;
    padding-top: 0;
    min-width: 0;
  }
`

export const SectionLabel = styled.div`
  font-size: 13px;
  font-family: ${({ theme }) => theme.typography.fontInter};
  font-weight: 700;
  margin-bottom: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1;
`

export const ClassroomCard = styled.div<{
  $isDisabled?: boolean
  $isSelected?: boolean
}>`
  width: 252px;
  height: 43px;
  margin-left: 10px;
  border-radius: 40px;
  background: ${({ $isDisabled, theme }) =>
    $isDisabled ? theme.colors.skeleton.primary : theme.colors.card.background};
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.75 : 1)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 14px;
  padding-right: 16px;
  box-sizing: border-box;
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  border: 1px solid
    ${(props) =>
      props.$isSelected ? 'transparent' : props.theme.colors.border.primary};

  @media (max-width: 640px) {
    margin-left: 0;
    width: 100%;
  }
`

export const ClassroomName = styled.div`
  font-size: 14px;
  font-family: ${({ theme }) => theme.typography.fontNunito};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const ObjectivesSection = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 10px;

  align-items: center;
  gap: 4px;
  min-width: 92px;

  @media (max-width: 640px) {
    grid-area: objectives;
    padding-top: 0;
    min-width: 0;
  }
`

export const ObjectiveCount = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1;
`

export const SummativesSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 10px;
  gap: 9px;
  min-width: 90px;

  @media (max-width: 640px) {
    grid-area: summatives;
    padding-top: 0;
    min-width: 0;
  }
`

export const SummativeLevels = styled.div`
  display: flex;
  margin-top: 3px;
  gap: 15px;
`

export const SummativeLevelText = styled.span<{ $isAssessed: boolean }>`
  font-size: 20px;
  color: ${({ $isAssessed, theme }) =>
    $isAssessed ? theme.colors.text.success : theme.colors.text.primary};
  opacity: ${({ $isAssessed }) => ($isAssessed ? 1 : 0.2)};
  font-family: ${({ theme }) => theme.typography.fontPlusJakartaSans};
  font-weight: 700;
`

export const RightSection = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  gap: 10px;

  @media (max-width: 640px) {
    grid-area: right;
    justify-content: center;
  }
`

export const ProfileButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: none;
  padding: 0;
  background: transparent;
  cursor: pointer;
`

export const UserBadge = styled.div<{
  $backgroundColor?: string
  $textColor?: string
}>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  font-family: ${({ theme }) => theme.typography.fontPlusJakartaSans};
  background-color: ${({ $backgroundColor, theme }) =>
    $backgroundColor || theme.colors.surface.error};
  color: ${({ $textColor }) => $textColor || 'white'};
  font-size: 16px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`

export const ProfileDropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 12px;
  width: 210px;
  padding: 10px;
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.card.background};
  box-shadow: 0 10px 24px rgba(23, 31, 46, 0.12);
  z-index: 30;
`

export const ProfileSwatchSection = styled.div`
  margin-left: 2px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 0 6px;
`

export const ProfileSwatchLabel = styled.span`
  color: ${({ theme }) => theme.colors.text.paragraph};
  font-family: ${({ theme }) => theme.typography.fontInter};
  font-size: 12px;
  font-weight: 400;
  line-height: 1;
`

export const ProfileSwatchGrid = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  flex-wrap: wrap;
`

export const ProfileSwatchWrapper = styled.div<{
  $isSelected: boolean
}>`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

export const ProfileSwatchButton = styled.button<{
  $color: string
  $isSelected: boolean
}>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  /**
   * The selected border follows the current mode locally so this dropdown can
   * use its design-specific grays without changing the shared theme contract.
   */
  border: 2px solid
    ${({ $isSelected, theme }) =>
      $isSelected
        ? theme.mode === 'dark'
          ? 'var(--color-gray-550, #B6B6B6)'
          : 'var(--color-gray-500, #CCCCCC)'
        : 'transparent'};
  /**
   * Only the active swatch receives the selection shadow so inactive colors
   * keep the same visual weight without changing any swatch dimensions.
   */
  box-shadow: ${({ $isSelected }) =>
    $isSelected ? '2px 2px 0 0 rgba(0, 0, 0, 0.25)' : 'none'};
  background-color: ${({ $color }) => $color};
  cursor: pointer;
  flex: 0 0 auto;
  transition: border-color 120ms ease;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.primary};
    outline-offset: 0;
  }
`

export const ProfileDropdownDivider = styled.div`
  width: 100%;
  height: 1px;
  background: ${({ theme }) => theme.colors.border.primary};
  margin: 6px 0;
`

export const ProfileDropdownRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 2px 2px 4px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.typography.fontNunito};
  font-size: 16px;
  font-weight: 400;
  line-height: 1;
  margin-top: 4px;
  margin-bottom: 4px;

  .switch {
    margin-bottom: 0;
  }

  span {
    color: ${({ theme }) => theme.colors.text.primary};
    font-family: ${({ theme }) => theme.typography.fontNunito};
    font-size: 16px;
    font-weight: 400;
    line-height: 1;
  }
`

export const ProfileDropdownButton = styled.button`
  width: 100%;
  border: none;
  padding: 2px 2px 2px 4px;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.typography.fontNunito};
  font-size: 16px;
  font-weight: 400;
  line-height: 1;
  text-align: left;
  cursor: pointer;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 10px;

  &:hover {
    background: ${({ theme }) => theme.colors.card.surface};
    border-radius: 8px;
  }
`

export const SkeletonBlock = styled.div<{
  width: string
  height: string
  radius?: string
}>`
  width: ${(props) => props.width};
  height: ${(props) => props.height};
  border-radius: ${(props) => props.radius || '8px'};
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.skeleton.primary} 8%,
    ${({ theme }) => theme.colors.card.surface} 18%,
    ${({ theme }) => theme.colors.skeleton.secondary} 33%
  );
  background-size: 800px 104px;
  animation: shimmer 1.2s infinite linear;
`

export const SkeletonCircle = styled(SkeletonBlock)`
  border-radius: 50%;
`

export const DropdownBackdrop = styled.div`
  position: fixed;
  inset: 0;
  appearance: none;
  border: none;
  padding: 0;
  margin: 0;
  background: rgba(23, 31, 46, 0.22);
  backdrop-filter: blur(5px);
  z-index: 10;

  &:focus,
  &:focus-visible,
  &:active {
    outline: none;
  }
`

export const DropdownOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: min(960px, calc(100vw - 24px));
  max-width: calc(100vw - 24px);
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 171.5px minmax(0, 1fr);
  z-index: 20;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    row-gap: 10px;
  }
`

export const DropdownTitle = styled.h3`
  margin-top: 25px;
  margin-left: 20px;
  color: #ffffff;
  font-family: ${({ theme }) => theme.typography.fontNunito};
  font-size: 20px;
  font-weight: 700;

  @media (max-width: 900px) {
    margin: 12px 0 0;
    text-align: center;
  }
`

export const DropdownPanels = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${HeaderSurface} {
    width: min(617px, 100%);
  }

  @media (max-width: 900px) {
    align-items: center;
  }
`
